import React from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import type { InterviewMessage } from '../../../generated-src/client/interview-settings/api';
import { apiService } from '../../services/apiService';

interface MessageInputProps {
  id: string;
  index: number;
  initialText: string;
  onRemove: (id: string) => void;
}

// Глобальное хранилище текстов вне React
const globalTexts: { [key: string]: string } = {};

const MessageInput: React.FC<MessageInputProps> = React.memo(({ 
  id, 
  index, 
  initialText, 
  onRemove
}) => {
  const colors = useBrandingColors();
  // Инициализируем из глобального хранилища или из initialText
  const [text, setText] = React.useState(() => {
    return globalTexts[id] || initialText;
  });

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, 40) + 'px';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    // Сразу сохраняем в глобальное хранилище
    globalTexts[id] = newText;
    adjustTextareaHeight(e.target);
  };

  // Автоматически подстраиваем высоту при монтировании компонента
  React.useEffect(() => {
    const textarea = document.querySelector(`textarea[data-id="${id}"]`) as HTMLTextAreaElement;
    if (textarea && text) {
      adjustTextareaHeight(textarea);
    }
  }, [id, text]);

  // Очищаем глобальное хранилище при размонтировании
  React.useEffect(() => {
    return () => {
      // НЕ удаляем при размонтировании, так как это может быть перерендер
      // delete globalTexts[id];
    };
  }, [id]);

  return (
    <div className="flex items-start gap-3 p-1">
      <div className="flex-1">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-[#525866]">
            Сообщение {index + 1}
          </label>
          <button
            onClick={() => onRemove(id)}
            className="text-sm font-medium transition-colors"
            style={{ color: colors.primary }}
            onMouseEnter={(e) => { e.currentTarget.style.color = colors.primaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = colors.primary; }}
          >
            Удалить
          </button>
        </div>
        <textarea
          data-id={id}
          value={text}
          onChange={handleChange}
          placeholder={`Введите вступительное сообщение ${index + 1}...`}
          className="w-full px-3 py-2 border border-[#cdd0d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e16349] focus:border-transparent resize-none overflow-hidden text-sm"
          rows={1}
          style={{ minHeight: '44px' }}
        />
      </div>
    </div>
  );
});

interface Props {
  embedded?: boolean;
}

export const WelcomeMessagesSection: React.FC<Props> = ({ embedded = false }) => {
  const [messages, setMessages] = React.useState<Array<{ id: string; text: string }>>([]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const colors = useBrandingColors();

  React.useEffect(() => {
    loadWelcome();
  }, []);

  const loadWelcome = async () => {
    try {
      const response = await apiService.getWelcomeMessages();
      const apiMessages = response.messages || [];
      const items = apiMessages.map((m: any, i: number) => ({
        id: `api-message-${i}-${Date.now()}`,
        text: m.text || ''
      }));
      setMessages(items.length > 0 ? items : [{ id: `default-message-${Date.now()}`, text: '' }]);
    } catch (error) {
      setError('Ошибка при загрузке вступления');
      setMessages([{ id: `error-message-${Date.now()}`, text: '' }]);
    }
  };

  const handleAddWelcome = () => {
    const newId = `message-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setMessages(prev => [...prev, { id: newId, text: '' }]);
  };

  const handleRemoveWelcome = (id: string) => {
    setMessages(prev => {
      if (prev.length > 1) {
        // Очищаем глобальное хранилище при удалении
        delete globalTexts[id];
        return prev.filter(msg => msg.id !== id);
      }
      return prev;
    });
  };



  const handleSaveAll = async () => {
    try {
      setError('');
      setSaving(true);
      
      // Получаем актуальные тексты из глобального хранилища
      const texts = messages
        .map(msg => globalTexts[msg.id] || msg.text || '')
        .filter(text => text.trim().length > 0);
      
      const welcomeMessagesData: InterviewMessage[] = texts.map((text: string, i: number) => ({
        text: text.trim(),
        orderIndex: i
      }));

      await apiService.updateWelcomeMessages({ messages: welcomeMessagesData });
      setError('');
    } catch (error) {
      setError('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    embedded ? <>{children}</> : <div className="bg-white rounded-lg border border-[#e2e4e9] p-6">{children}</div>
  );

  return (
    <Wrapper>
      {!embedded && <h2 className="text-xl font-semibold text-[#000] mb-6">Вступительные сообщения</h2>}

      <div className="space-y-4 mb-6">
        {messages.length === 0 && (
          <button
            onClick={handleAddWelcome}
            className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-[14px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
          >
            <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                  Добавить сообщение
                </p>
              </div>
            </div>
          </button>
        )}

        {messages.map((message, index) => (
          <MessageInput
            key={message.id}
            id={message.id}
            index={index}
            initialText={message.text}
            onRemove={handleRemoveWelcome}
          />
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        {messages.length > 0 && (
          <div className="flex gap-3">
            <button
              onClick={handleAddWelcome}
              className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1 items-center justify-center overflow-clip p-[14px] relative rounded-2xl shrink-0 hover:bg-[#f6f8fa] transition-colors cursor-pointer h-[44px]"
            >
              <div className="box-border content-stretch flex flex-row gap-2 items-center justify-center px-1 py-0 relative shrink-0">
                <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                  <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                    Добавить сообщение
                  </p>
                </div>
              </div>
            </button>
            <button
              onClick={handleSaveAll}
              className="px-6 py-2 text-white rounded-2xl transition-colors h-[44px]"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
              disabled={saving}
            >
              {saving ? 'Сохраняем...' : 'Сохранить'}
            </button>
          </div>
        )}
        {messages.length === 0 && (
          <button
            onClick={handleSaveAll}
            className="px-6 py-2 text-white rounded-2xl transition-colors h-[44px]"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : 'Сохранить'}
          </button>
        )}
      </div>
    </Wrapper>
  );
};
